import { useEffect, useMemo, useRef, useState } from "react";
import { buildPreview, PREVIEW_SANDBOX } from "@buildlab/preview";
import type { FileMap } from "@buildlab/shared";
import { api, type Checkpoint } from "./api";
import { History, Play, Restart } from "./icons";
import { useFeature } from "./features";
import { TimeLapse } from "./TimeLapse";

// History (version timeline): every saved version of the project, oldest to newest, with a live
// preview of each, what changed between versions, a play-through, and "Go back to this version".
// Versions come from the checkpoints saved before each change the helper makes (FR-9).

interface Version {
  /** null = the project as it is now. */
  id: number | null;
  label: string;
  /** The change that came right after this version, in the student's words. */
  then: string;
  createdAt: number;
}

/** Lines added and removed per file between two versions (small files, so a plain LCS is fine). */
export function changedFiles(before: FileMap, after: FileMap): { path: string; added: number; removed: number }[] {
  const out: { path: string; added: number; removed: number }[] = [];
  for (const path of [...new Set([...Object.keys(before), ...Object.keys(after)])].sort()) {
    const a = (before[path] ?? "").split("\n");
    const b = (after[path] ?? "").split("\n");
    if (before[path] === after[path]) continue;
    if (before[path] === undefined) {
      out.push({ path, added: b.length, removed: 0 });
      continue;
    }
    if (after[path] === undefined) {
      out.push({ path, added: 0, removed: a.length });
      continue;
    }
    const n = a.length;
    const m = b.length;
    let prev = new Array<number>(m + 1).fill(0);
    for (let i = 1; i <= n; i++) {
      const cur = new Array<number>(m + 1).fill(0);
      for (let j = 1; j <= m; j++) cur[j] = a[i - 1] === b[j - 1] ? prev[j - 1]! + 1 : Math.max(prev[j]!, cur[j - 1]!);
      prev = cur;
    }
    const same = prev[m]!;
    out.push({ path, added: m - same, removed: n - same });
  }
  return out;
}

const PLAN = "Before: approved plan";
const describe = (reason: string) =>
  reason === PLAN ? "Before the helper built your plan" : reason.startsWith("Before: ") ? `Before you asked: “${reason.slice(8)}”` : reason === "Before restoring an earlier version" ? "Before going back to an earlier version" : reason;

const timeOf = (t: number) => new Date(t).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });

export function HistoryView({ projectId, files, disabled, onRestore }: { projectId: string; files: FileMap; disabled: boolean; onRestore: (files: FileMap) => void }) {
  const [checkpoints, setCheckpoints] = useState<Checkpoint[] | null>(null);
  const [selected, setSelected] = useState<number | null>(null); // index into versions
  const [cache, setCache] = useState<Record<number, FileMap>>({});
  const [playing, setPlaying] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [lapse, setLapse] = useState(false);
  const timeLapseOn = useFeature("timeLapse");
  const [error, setError] = useState<string | null>(null);
  const strip = useRef<HTMLOListElement>(null);

  const load = () =>
    api
      .checkpoints(projectId)
      .then(({ checkpoints }) => setCheckpoints(checkpoints))
      .catch((e: Error) => setError(e.message));
  useEffect(() => {
    void load();
  }, [projectId]);

  // Oldest first, then "Now".
  const versions: Version[] = useMemo(
    () =>
      [...(checkpoints ?? [])]
        .reverse()
        .map((c): Version => ({
          id: c.id,
          label: describe(c.reason),
          then: c.reason === PLAN ? "Then the helper built the plan you approved." : c.reason.startsWith("Before: ") ? `Then you asked: “${c.reason.slice(8)}”` : c.reason === "Before restoring an earlier version" ? "Then you went back to an earlier version." : "Then:",
          createdAt: c.createdAt,
        }))
        .concat([{ id: null, label: "Now", then: "", createdAt: Date.now() }]),
    [checkpoints],
  );
  const index = selected ?? Math.max(0, versions.length - 2);
  const version = versions[index];
  const next = versions[index + 1];

  // Fetch the files for the selected version and the one after it (for "what changed").
  useEffect(() => {
    for (const v of [version, next]) {
      if (v?.id != null && !cache[v.id]) {
        const id = v.id;
        api
          .checkpoint(projectId, id)
          .then(({ files }) => setCache((c) => ({ ...c, [id]: files })))
          .catch((e: Error) => setError(e.message));
      }
    }
  }, [version?.id, next?.id]);

  const filesOf = (v: Version | undefined) => (v ? (v.id == null ? files : cache[v.id]) : undefined);
  const shown = filesOf(version);
  const after = filesOf(next);
  const preview = useMemo(() => (shown ? buildPreview(shown).html : ""), [shown]);
  const changes = useMemo(() => (shown && after ? changedFiles(shown, after) : []), [shown, after]);

  // Play through: step to the next version every 1.8 s.
  useEffect(() => {
    if (!playing) return;
    if (index >= versions.length - 1) {
      setPlaying(false);
      return;
    }
    const t = setTimeout(() => setSelected(index + 1), 1800);
    return () => clearTimeout(t);
  }, [playing, index, versions.length]);

  useEffect(() => {
    strip.current?.querySelector(".on")?.scrollIntoView({ block: "nearest", inline: "center" });
  }, [index]);

  if (error) return <p className="page-error history-msg">{error}</p>;
  if (!checkpoints) return <p className="loading history-msg">Loading your history…</p>;

  return (
    <div className="history">
      {lapse && <TimeLapse projectId={projectId} files={files} onClose={() => setLapse(false)} />}
      <section className="panel timeline" aria-label="Versions">
        <div className="row between">
          <span className="eyebrow">History · every version of your project</span>
          <div className="row">
            <button
              type="button"
              className="outline small"
              disabled={versions.length < 2}
              onClick={() => {
                if (playing) setPlaying(false);
                else {
                  setSelected(index >= versions.length - 1 ? 0 : index);
                  setPlaying(true);
                }
              }}
            >
              <Play size={12} />
              {playing ? "Pause" : "Play through"}
            </button>
            {timeLapseOn && (
              <button type="button" className="outline small" disabled={checkpoints.length === 0} onClick={() => setLapse(true)}>
                <Play size={12} />
                Time-lapse
              </button>
            )}
          </div>
        </div>
        {versions.length === 1 ? (
          <p className="hint">No earlier versions yet. Each time the helper changes your project, the version from before is saved here.</p>
        ) : (
          <ol className="dots" ref={strip}>
            {versions.map((v, i) => (
              <li key={v.id ?? "now"} className={`${i === index ? "on" : ""}${v.id == null ? " now" : ""}`}>
                <button type="button" onClick={() => (setPlaying(false), setSelected(i))} aria-current={i === index} title={v.label}>
                  <span className="dot" aria-hidden="true" />
                  <span className="vlabel">{v.id == null ? "Now" : `Version ${i + 1}`}</span>
                  <span className="vtime">{v.id == null ? "" : timeOf(v.createdAt)}</span>
                </button>
              </li>
            ))}
          </ol>
        )}
      </section>

      <section className="panel version" aria-label="This version">
        <div className="row between">
          <div>
            <span className="eyebrow">{version?.id == null ? "Now" : `Version ${index + 1} · ${timeOf(version.createdAt)}`}</span>
            <h2>{version?.label}</h2>
          </div>
          {version?.id != null && (
            <div className="row">
              {confirm ? (
                <>
                  <button
                    type="button"
                    className="primary small"
                    disabled={disabled}
                    onClick={async () => {
                      const { files: restored } = await api.restore(projectId, version.id!);
                      onRestore(restored);
                      setConfirm(false);
                      setSelected(null);
                      await load();
                    }}
                  >
                    Yes, go back
                  </button>
                  <button type="button" className="linklike" onClick={() => setConfirm(false)}>
                    Cancel
                  </button>
                </>
              ) : (
                <button type="button" className="outline small" disabled={disabled} onClick={() => setConfirm(true)}>
                  <History size={14} />
                  Go back to this version
                </button>
              )}
            </div>
          )}
        </div>
        {confirm && <p className="hint">Your project goes back to this version. The way it is now is saved first, so you can come back to it.</p>}
        <div className="vstage">
          {shown ? (
            <iframe key={`${version?.id ?? "now"}`} title="This version of your project" sandbox={PREVIEW_SANDBOX} srcDoc={preview} />
          ) : (
            <p className="loading">Loading this version…</p>
          )}
        </div>
      </section>

      <section className="panel changes" aria-label="What changed next">
        <span className="eyebrow">What changed next</span>
        {next ? (
          <>
            <p className="ink2">{version?.then}</p>
            <p className="hint">{next.id == null ? "What's different now:" : `What's different in version ${index + 2}:`}</p>
            {after && changes.length === 0 && <p className="hint">Nothing changed in the files.</p>}
            <ul className="diff">
              {changes.map((d) => (
                <li key={d.path}>
                  <code>{d.path}</code>
                  <span className="plus">+{d.added}</span>
                  <span className="minus">−{d.removed}</span>
                </li>
              ))}
            </ul>
            <button type="button" className="linklike" onClick={() => (setPlaying(false), setSelected(index + 1))}>
              Next version →
            </button>
          </>
        ) : (
          <p className="hint">This is how your project is now.</p>
        )}
        {index > 0 && (
          <button type="button" className="linklike muted-link" onClick={() => (setPlaying(false), setSelected(index - 1))}>
            <Restart size={12} /> Previous version
          </button>
        )}
      </section>
    </div>
  );
}
